import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Slider } from "../ui/slider";
import { Download, Upload, Plus, User, Check, X, Users } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { jsPDF } from "jspdf";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import BoardMemberPopup from "./BoardMemberPopup";
import {
  fetchBoardMembers,
  createBoardMember,
  updateBoardMember,
  deleteBoardMember,
  uploadDocument,
  fetchDocuments,
  downloadDocument,
  deleteDocument,
} from "../../services/businessDashApi";

const BoardMemberGallery = () => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Load board members from API
  useEffect(() => {
    loadBoardMembers();
  }, []);

  const loadBoardMembers = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      const boardMembers = await fetchBoardMembers();
      // ✅ Only show approved and active board members
      const approvedMembers = (Array.isArray(boardMembers) ? boardMembers : (boardMembers.data || [])).filter(
        (member) => member.approval_status === "approved" && member.is_active
      );
      setMembers(approvedMembers);
    } catch (error) {
      console.error("Failed to load board members:", error);
      setLoadError("Failed to load board members");
    } finally {
      setIsLoading(false);
    }
  };

  // Keep old static data for reference (remove later)
  const [_oldMembers] = useState([
    {
      id: "1",
      name: "John Smith",
      role: "president",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      email: "john.smith@company.com",
      company: "TechCorp Inc.",
      degree: "MBA",
      institution: "Harvard Business School",
      tenureStartDate: "2020-01-15",
      expertise: ["Technology Strategy", "Finance"],
      leisureActivities: ["Reading", "Golf", "Traveling"],
    },
    {
      id: "2",
      name: "Sarah Johnson",
      role: "ordinary",
      image: "/lovable-uploads/aa2423e1-7f80-498a-9d58-b377e2266f62.png",
      email: "sarah.johnson@legal.com",
      company: "Legal Associates",
      degree: "J.D.",
      institution: "Stanford Law School",
      tenureStartDate: "2021-03-01",
      expertise: ["Legal", "Compliance"],
      leisureActivities: ["Yoga", "Cooking", "Art"],
    },
    {
      id: "3",
      name: "Michael Chen",
      role: "employee",
      image:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      email: "michael.chen@company.com",
      company: "Internal",
      degree: "B.Sc.",
      institution: "MIT",
      tenureStartDate: "2022-06-01",
      expertise: ["Human Resources", "Operations"],
      leisureActivities: ["Sports", "Gaming", "Music"],
    },
    {
      id: "4",
      name: "Emma Davis",
      role: "ordinary",
      image: "/lovable-uploads/0c187ea9-50a0-4ea3-856e-b22b5943f73a.png",
      email: "emma.davis@finance.com",
      company: "FinanceFirst Ltd.",
      degree: "CPA",
      institution: "Wharton School",
      tenureStartDate: "2019-09-01",
      expertise: ["Accounting", "Finance"],
      leisureActivities: ["Reading", "Gardening", "Photography"],
    },
    {
      id: "5",
      name: "Alexander Rodriguez",
      role: "ordinary",
      image:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=face",
      email: "alex.rodriguez@company.com",
      company: "External Advisory",
      degree: "Ph.D.",
      institution: "MIT",
      tenureStartDate: "2023-02-15",
      expertise: ["Innovation", "Technology"],
      leisureActivities: ["Research", "Cycling", "Mentoring"],
    },
    { id: "6", name: "" },
    { id: "7", name: "" },
    { id: "8", name: "" },
  ]);

  const [selectedMember, setSelectedMember] = useState(null);
  const [isFileCardOpen, setIsFileCardOpen] = useState(false);
  const [expertisePoints, setExpertisePoints] = useState({});
  const [selectedLeisureActivities, setSelectedLeisureActivities] = useState(
    []
  );
  const [formFields, setFormFields] = useState({
    lgbtqiaIdentity: "",
    specialRequirements: "",
    dietaryRequirements: "",
    degreeYear: "",
    institutionCountry: "",
    degree: "",
  });
  const [dietaryDocuments, setDietaryDocuments] = useState([]);
  const [children, setChildren] = useState([]);

  // Refs for file uploads
  const profileImageInputRef = useRef(null);
  const dietaryDocumentRef = useRef(null);

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "president":
        return "bg-yellow-500";
      case "vice-president":
        return "bg-blue-500";
      case "employee":
        return "bg-green-500";
      case "ordinary":
        return "bg-gray-400";
      default:
        return "bg-muted";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "president":
        return "Chairperson";
      case "vice-president":
        return "Vice Chair";
      case "employee":
        return "Employee Member";
      case "ordinary":
      default:
        return "Ordinary Member";
    }
  };

  const calculateTenure = (startDate) => {
    if (!startDate) return "0 years";
    const start = new Date(startDate);
    const now = new Date();
    const years = Math.floor(
      (now.getTime() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
    );
    return `${years} year${years !== 1 ? "s" : ""}`;
  };

  const exportMemberAsPdf = () => {
    if (!selectedMember) return;

    const safeGet = (id) => document.getElementById(id)?.value ?? "";

    const data = {
      name: safeGet("fullName") || selectedMember.name || "",
      role: selectedMember.role || "ordinary",
      email: safeGet("email") || selectedMember.email || "",
      company: safeGet("currentEmployer") || selectedMember.company || "",
      tenureStartDate: safeGet("tenureStart") || selectedMember.tenureStartDate || "",
      address_line_1: safeGet("address1") || selectedMember.address_line_1 || "",
      address_line_2: safeGet("address2") || selectedMember.address_line_2 || "",
      country: safeGet("country") || selectedMember.country || "",
      extension: safeGet("extension") || selectedMember.extension || "",
      phone: safeGet("phone") || selectedMember.phone || "",
      ice_contact_name: safeGet("iceContact") || selectedMember.ice_contact_name || "",
      ice_contact_phone: safeGet("icePhone") || selectedMember.ice_contact_phone || "",
      institution: safeGet("institution") || selectedMember.institution || "",
      degree: formFields.degree || selectedMember.degree || "",
      degree_year: formFields.degreeYear || selectedMember.degree_year || "",
      institution_country:
        formFields.institutionCountry || selectedMember.institution_country || "",
      lgbtqia: formFields.lgbtqiaIdentity || selectedMember.lgbtqia || "",
      special_requirements:
        formFields.specialRequirements || selectedMember.special_requirements || "",
      dietary_requirements:
        formFields.dietaryRequirements || selectedMember.dietary_requirements || "",
      expertisePoints,
      leisureActivities: selectedLeisureActivities,
      children,
    };
    const roleLabel = getRoleLabel(data.role);
    const slug = (s) =>
      String(s || "member")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const lineHeight = 16;
    let y = margin;

    const ensureSpace = (needed = lineHeight) => {
      if (y + needed > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addHeading = (text) => {
      ensureSpace(24);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(text, margin, y);
      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
    };

    const addKV = (label, value) => {
      const v = String(value ?? "").trim();
      if (!v) return;
      ensureSpace(24);
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, margin, y);
      doc.setFont("helvetica", "normal");
      const wrapped = doc.splitTextToSize(v, pageWidth - margin * 2 - 90);
      doc.text(wrapped, margin + 90, y);
      y += lineHeight * wrapped.length;
    };

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Board Member Profile", margin, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(new Date().toLocaleString(), margin, y);
    doc.setTextColor(0);
    y += 18;

    addHeading("Member");
    addKV("Name", data.name);
    addKV("Member Type", roleLabel);
    addKV("Email", data.email);
    addKV(
      "Phone",
      `${data.extension ? `${data.extension} ` : ""}${data.phone || ""}`.trim()
    );
    addKV("Company", data.company);
    addKV("Tenure Start", data.tenureStartDate);

    addHeading("Address");
    addKV("Address Line 1", data.address_line_1);
    addKV("Address Line 2", data.address_line_2);
    addKV("Country", data.country);

    addHeading("ICE Contact");
    addKV("ICE Name", data.ice_contact_name);
    addKV("ICE Phone", data.ice_contact_phone);

    addHeading("Education");
    addKV("Highest Degree", data.degree);
    addKV("Year of Degree", data.degree_year);
    addKV("Institution", data.institution);
    addKV("Institution Country", data.institution_country);

    addHeading("Preferences");
    addKV("LGBTQIA+", data.lgbtqia);
    addKV("Special Requirements", data.special_requirements);
    addKV("Dietary Requirements", data.dietary_requirements);

    addHeading("Expertise (0-7)");
    const expertiseEntries = Object.entries(data.expertisePoints || {}).filter(
      ([, points]) => Number(points) > 0
    );
    if (expertiseEntries.length === 0) {
      addKV("Expertise", "None");
    } else {
      expertiseEntries.forEach(([area, points]) => addKV(area, `${points}/7`));
    }

    addHeading("Leisure Activities");
    if (!data.leisureActivities?.length) {
      addKV("Leisure", "None");
    } else {
      data.leisureActivities.forEach((a) => addKV("-", a));
    }

    addHeading("Children");
    if (!data.children?.length) {
      addKV("Children", "None");
    } else {
      data.children.forEach((c) =>
        addKV("-", `${c?.name || ""}${c?.age ? ` (${c.age})` : ""}`.trim())
      );
    }

    doc.save(`board-member-${slug(data.name)}.pdf`);
  };

  // Fetch dietary documents for a specific member
  const loadDietaryDocuments = async (memberId) => {
    try {
      const filters = {
        board_member_id: memberId,
        document_type: "dietary_requirements",
      };
      const docs = await fetchDocuments(filters);
      setDietaryDocuments(docs || []);
    } catch (error) {
      console.error("Error loading dietary documents:", error);
      setDietaryDocuments([]);
    }
  };

  const handleMemberClick = (member) => {
    if (member.name) {
      setSelectedMember(member);

      // Initialize expertise points from member data
      const points = {};
      if (member.expertise && Array.isArray(member.expertise)) {
        member.expertise.forEach((exp) => {
          if (typeof exp === "object" && exp.area && exp.points) {
            points[exp.area] = exp.points;
          } else if (typeof exp === "string") {
            points[exp] = 5; // Default points if only area name provided
          }
        });
      }
      setExpertisePoints(points);

      // Initialize leisure activities
      setSelectedLeisureActivities(member.leisureActivities || []);

      // Initialize children
      setChildren(member.children || []);

      // Initialize other form fields
      setFormFields({
        lgbtqiaIdentity: member.lgbtqiaIdentity || member.lgbtqia || "",
        specialRequirements:
          member.specialRequirements || member.special_requirements || "",
        dietaryRequirements:
          member.dietaryRequirements || member.dietary_requirements || "",
        degreeYear: member.degreeYear || member.degree_year || "",
        institutionCountry:
          member.institutionCountry || member.institution_country || "",
        degree: member.degree || "",
      });

      // Load dietary documents if member has an ID
      if (member.id) {
        loadDietaryDocuments(member.id);
      } else {
        setDietaryDocuments([]);
      }

      setIsFileCardOpen(true);
    }
  };

  // Handle profile image selection
  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image file size must be less than 5MB");
      return;
    }

    // Create a temporary URL for preview
    const imageUrl = URL.createObjectURL(file);

    // Update selectedMember with the new image URL
    setSelectedMember({
      ...selectedMember,
      image: imageUrl,
      imageFile: file, // Store the file for later upload
    });

    console.log("✅ Profile image selected:", file.name);
  };

  // Handle dietary document upload
  const handleDietaryDocumentChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF, DOCX, etc.)
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      alert("Please select a valid document file (PDF, DOC, or DOCX)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("Document file size must be less than 10MB");
      return;
    }

    // Store the file for later upload
    setSelectedMember({
      ...selectedMember,
      dietaryDocumentFile: file,
    });

    console.log("✅ Dietary document selected:", file.name);
    alert(
      `Document "${file.name}" selected and will be uploaded when you save.`
    );
  };

  // Handle download dietary document
  const handleDownloadDietaryDocument = async (documentId, documentName) => {
    try {
      const blob = await downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = documentName || "dietary-requirements.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      alert("Failed to download document. Please try again.");
    }
  };

  // Handle delete dietary document
  const handleDeleteDietaryDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) {
      return;
    }

    try {
      await deleteDocument(documentId);
      // Reload documents
      if (selectedMember?.id) {
        await loadDietaryDocuments(selectedMember.id);
      }
      alert("Document deleted successfully!");
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Failed to delete document. Please try again.");
    }
  };

  // Handle save member (create or update)
  const handleSaveMember = async () => {
    if (!selectedMember) return;

    try {
      // Convert expertise points to proper format: {area: string, points: number}[]
      const expertiseArray = Object.entries(expertisePoints)
        .filter(([area, points]) => points > 0) // Only include expertise with points > 0
        .map(([area, points]) => ({ area, points }));

      // Collect form data from the dialog inputs - ALL FIELDS
      const memberData = {
        // Contact Information
        name: document.getElementById("fullName")?.value || selectedMember.name,
        email: document.getElementById("email")?.value || selectedMember.email,
        address_line_1:
          document.getElementById("address1")?.value ||
          selectedMember.address_line_1 ||
          "",
        address_line_2:
          document.getElementById("address2")?.value ||
          selectedMember.address_line_2 ||
          "",
        country:
          document.getElementById("country")?.value ||
          selectedMember.country ||
          "",
        extension:
          document.getElementById("extension")?.value ||
          selectedMember.extension ||
          "",
        phone:
          document.getElementById("phone")?.value || selectedMember.phone || "",
        ice_contact_name:
          document.getElementById("iceContact")?.value ||
          selectedMember.ice_contact_name ||
          "",
        ice_contact_phone:
          document.getElementById("icePhone")?.value ||
          selectedMember.ice_contact_phone ||
          "",

        // Professional Information
        role: selectedMember.role || "ordinary",
        company:
          document.getElementById("currentEmployer")?.value ||
          selectedMember.company ||
          "",
        degree: formFields.degree || selectedMember.degree || "",
        degree_year: formFields.degreeYear
          ? parseInt(formFields.degreeYear)
          : selectedMember.degree_year || null,
        institution:
          document.getElementById("institution")?.value ||
          selectedMember.institution ||
          "",
        institution_country:
          formFields.institutionCountry ||
          selectedMember.institution_country ||
          "",
        tenureStartDate:
          document.getElementById("tenureStart")?.value ||
          selectedMember.tenureStartDate ||
          new Date().toISOString().split("T")[0],

        // Personal Information
        age:
          parseInt(document.getElementById("age")?.value) ||
          selectedMember.age ||
          null,
        sector: selectedMember.sector || "",
        bio: selectedMember.bio || "",
        lgbtqia: formFields.lgbtqiaIdentity || "",
        special_requirements: formFields.specialRequirements || "",
        dietary_requirements: formFields.dietaryRequirements || "",
        spouse_name:
          document.getElementById("spouseName")?.value ||
          selectedMember.spouse_name ||
          "",
        spouse_age:
          parseInt(document.getElementById("spouseAge")?.value) ||
          selectedMember.spouse_age ||
          null,

        // Arrays
        expertise: expertiseArray,
        leisureActivities: selectedLeisureActivities,
        children: children,
      };

      // Check if this is a new member (no id = new member)
      const isNew = !selectedMember.id;

      // Save member (with or without image)
      let savedMember;

      if (selectedMember.imageFile) {
        // Has image file - use TWO-STEP approach
        // Step 1: Save member data as JSON (with profile_image_url cleared)
        console.log("📤 Step 1: Saving member data (JSON)...");

        const memberDataWithoutUrl = {
          ...memberData,
          profile_image_url: null, // Clear external URL so uploaded file takes priority
        };

        console.log("📋 Member data:", memberDataWithoutUrl);

        if (isNew) {
          savedMember = await createBoardMember(memberDataWithoutUrl);
          console.log("✅ Board member created!");
        } else {
          savedMember = await updateBoardMember(
            selectedMember.id,
            memberDataWithoutUrl
          );
          console.log("✅ Board member updated!");
        }

        // Step 2: Upload image separately
        console.log("📤 Step 2: Uploading profile image...");
        const imageFormData = new FormData();
        imageFormData.append("profile_image", selectedMember.imageFile);

        savedMember = await updateBoardMember(savedMember.id, imageFormData);
        console.log("✅ Profile image uploaded!");
      } else {
        // No image file - send regular JSON
        console.log("📤 Sending JSON payload to backend:", memberData);

        if (isNew) {
          savedMember = await createBoardMember(memberData);
          console.log("✅ Board member created successfully!");
        } else {
          savedMember = await updateBoardMember(selectedMember.id, memberData);
          console.log("✅ Board member updated successfully!");
        }
      }

      // Upload dietary document separately if present
      if (selectedMember.dietaryDocumentFile && savedMember.id) {
        try {
          const docFormData = new FormData();
          docFormData.append("file", selectedMember.dietaryDocumentFile);
          docFormData.append("name", selectedMember.dietaryDocumentFile.name);
          docFormData.append("document_type", "dietary_requirements");
          docFormData.append("board_member_id", savedMember.id);

          await uploadDocument(docFormData);
          console.log("✅ Dietary document uploaded successfully!");

          // Reload dietary documents to show the new one
          await loadDietaryDocuments(savedMember.id);
        } catch (docError) {
          console.error("Failed to upload dietary document:", docError);
          alert(
            "Member saved, but dietary document upload failed. Please try again from Document Manager."
          );
        }
      }

      // Update local state
      if (isNew) {
        setMembers([...members, savedMember]);
      } else {
        setMembers(
          members.map((m) => (m.id === selectedMember.id ? savedMember : m))
        );
      }

      setIsFileCardOpen(false);
      loadBoardMembers(); // Refresh the list
    } catch (error) {
      console.error("Failed to save board member:", error);
      alert("Failed to save board member. Please try again.");
    }
  };

  const lgbtqiaOptions = [
    "Yes",
    "No",
    "Lesbian",
    "Gay",
    "Bisexual",
    "Transgender",
    "Queer",
    "Intersex",
    "Asexual",
    "Prefer not to say",
  ];

  const expertiseOptions = [
    "Accounting",
    "Legal",
    "Marketing",
    "Sales",
    "Sustainability",
    "Technology",
    "Finance",
    "Operations",
    "Human Resources",
    "Strategy",
    "Risk Management",
    "Sourcing",
    "Design",
    "Lobbyism",
    "Funding",
    "Industry knowledge",
  ];

  const leisureActivities = [
    "Reading",
    "Sports",
    "Music",
    "Traveling",
    "Cooking",
    "Gardening",
    "Photography",
    "Hiking",
    "Art",
    "Gaming",
    "Dancing",
    "Writing",
    "Fitness",
    "Yoga",
    "Swimming",
  ];

  return (
    <>
      {/* Horizontal Board Member Gallery */}
      <div className="flex flex-wrap justify-center gap-6 mb-8 max-w-full">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex flex-col items-center space-y-2 flex-shrink-0"
            style={{ width: "96px", minWidth: "96px" }}
          >
            <div
              className="relative rounded-full bg-muted/30 border-2 border-muted/40 flex items-center justify-center hover:border-primary/40 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-primary/20 group cursor-pointer"
              onClick={() => handleMemberClick(member)}
              style={{
                width: "80px",
                height: "80px",
                minWidth: "80px",
                minHeight: "80px",
                maxWidth: "80px",
                maxHeight: "80px",
              }}
            >
              {member.image ? (
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full rounded-full object-cover group-hover:brightness-110 transition-all duration-300"
                />
              ) : (
                <User className="w-20 h-10 text-muted-foreground group-hover:text-primary/60 transition-colors" />
              )}

              {/* Role badge */}
              {member.role && (
                <div
                  className={`absolute -bottom-1 -right-1 w-5 h-5 ${getRoleBadgeColor(
                    member.role
                  )} rounded-full border-2 border-background`}
                  title={getRoleLabel(member.role)}
                ></div>
              )}
            </div>

            <p className="text-xs text-foreground/80 font-medium min-h-[1rem] text-center">
              {member.name || "Add Member"}
            </p>
          </div>
        ))}

        {/* Add more members button */}
        <div
          className="flex flex-col items-center space-y-2 flex-shrink-0"
          style={{ width: "96px", minWidth: "96px" }}
        >
          <div
            className="relative rounded-full bg-muted/30 border-2 border-dashed border-muted/40 flex items-center justify-center hover:border-primary/40 transition-all duration-300 hover:scale-110 hover:shadow-lg hover:shadow-primary/20 cursor-pointer"
            onClick={() => {
              setSelectedMember({
                name: "",
                email: "",
                role: "ordinary",
              });
              setExpertisePoints({}); // Initialize empty expertise points for new member
              setSelectedLeisureActivities([]); // Reset leisure activities
              setChildren([]); // Reset children for new member
              setFormFields({
                lgbtqiaIdentity: "",
                specialRequirements: "",
                dietaryRequirements: "",
                degreeYear: "",
                institutionCountry: "",
                degree: "",
              }); // Reset all form fields
              setDietaryDocuments([]); // Reset dietary documents for new member
              setIsFileCardOpen(true);
            }}
            style={{
              width: "80px",
              height: "80px",
              minWidth: "80px",
              minHeight: "80px",
              maxWidth: "80px",
              maxHeight: "80px",
            }}
          >
            <Plus className="w-20 h-10 text-muted-foreground hover:text-primary/60 transition-colors" />
          </div>
          <p className="text-xs text-foreground/80 font-medium">Add More</p>
        </div>

        {/* Board Overview Button */}
        <div className="flex flex-col items-center space-y-2 flex-shrink-0">
          <div className="flex-shrink-0">
            <BoardMemberPopup />
          </div>
        </div>
      </div>

      {/* Color key below the grid */}
      <div className="flex justify-center mb-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span>Chairperson</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>Vice Chair</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Employee Member</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span>Ordinary Member</span>
          </div>
        </div>
      </div>

      {/* File Card Dialog */}
      <Dialog open={isFileCardOpen} onOpenChange={setIsFileCardOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <User className="w-5 h-5 text-primary" />
              Board Member File Card
            </DialogTitle>
          </DialogHeader>

          {selectedMember && (
            <>
              {/* Member Type */}
              <div className="px-4 pt-4">
                <Label htmlFor="memberType">Member Type</Label>
                <Select
                  value={selectedMember.role || "ordinary"}
                  onValueChange={(value) =>
                    setSelectedMember({ ...selectedMember, role: value })
                  }
                >
                  <SelectTrigger id="memberType" className="mt-2">
                    <SelectValue placeholder="Select member type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="president">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-500" />
                        Chairperson
                      </span>
                    </SelectItem>
                    <SelectItem value="vice-president">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        Vice Chair
                      </span>
                    </SelectItem>
                    <SelectItem value="employee">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        Employee Member
                      </span>
                    </SelectItem>
                    <SelectItem value="ordinary">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-gray-400" />
                        Ordinary Member
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-6 p-4">
                {/* Column 1: Contact Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground border-b pb-2">
                    Contact Information
                  </h4>

                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="fullName">Full Name</Label>
                      <Input id="fullName" defaultValue={selectedMember.name} />
                    </div>

                    <div>
                      <Label htmlFor="address1">Address Line 1</Label>
                      <Input
                        id="address1"
                        defaultValue={selectedMember.address_line_1 || ""}
                      />
                    </div>

                    <div>
                      <Label htmlFor="address2">Address Line 2</Label>
                      <Input
                        id="address2"
                        defaultValue={selectedMember.address_line_2 || ""}
                      />
                    </div>

                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        defaultValue={selectedMember.country || ""}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="extension">Extension</Label>
                        <Input
                          id="extension"
                          defaultValue={selectedMember.extension || ""}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          defaultValue={selectedMember.phone || ""}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        defaultValue={selectedMember.email || ""}
                      />
                    </div>

                    <div>
                      <Label htmlFor="iceContact">ICE Contact Name</Label>
                      <Input
                        id="iceContact"
                        defaultValue={selectedMember.ice_contact_name || ""}
                      />
                    </div>

                    <div>
                      <Label htmlFor="icePhone">ICE Phone Number</Label>
                      <Input
                        id="icePhone"
                        placeholder="Emergency contact phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="currentEmployer">Current Employer</Label>
                      <Input
                        id="currentEmployer"
                        defaultValue={selectedMember.company || ""}
                      />
                    </div>

                    <div>
                      <Label htmlFor="tenureStart">Tenure Start Date</Label>
                      <Input
                        id="tenureStart"
                        type="date"
                        defaultValue={selectedMember.tenureStartDate || ""}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Current tenure:{" "}
                        {calculateTenure(selectedMember.tenureStartDate)}
                      </p>
                    </div>

                    <div>
                      <Label>Family Names</Label>
                      <div className="space-y-2 mt-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            id="spouseName"
                            placeholder="Spouse name"
                            defaultValue={
                              selectedMember.spouseName ||
                              selectedMember.spouse_name ||
                              ""
                            }
                          />
                          <Input
                            id="spouseAge"
                            placeholder="Spouse age"
                            type="number"
                            defaultValue={
                              selectedMember.spouseAge ||
                              selectedMember.spouse_age ||
                              ""
                            }
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Children</Label>
                          <div className="space-y-2 mt-1">
                            {children.map((child, index) => (
                              <div
                                key={index}
                                className="grid grid-cols-[1fr,1fr,auto] gap-2 items-center"
                              >
                                <Input
                                  placeholder="Child name"
                                  value={child.name || ""}
                                  onChange={(e) => {
                                    const updatedChildren = [...children];
                                    updatedChildren[index].name =
                                      e.target.value;
                                    setChildren(updatedChildren);
                                  }}
                                />
                                <Input
                                  placeholder="Age"
                                  type="number"
                                  value={child.age || ""}
                                  onChange={(e) => {
                                    const updatedChildren = [...children];
                                    updatedChildren[index].age = parseInt(
                                      e.target.value
                                    );
                                    setChildren(updatedChildren);
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={() => {
                                    setChildren(
                                      children.filter((_, i) => i !== index)
                                    );
                                  }}
                                >
                                  <X className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            ))}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 mt-2"
                              onClick={() => {
                                setChildren([
                                  ...children,
                                  { name: "", age: null },
                                ]);
                              }}
                            >
                              <Plus className="w-4 h-4" />
                              Add Child
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Column 2: Professional Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground border-b pb-2">
                    Professional Information
                  </h4>

                  <div className="space-y-3">
                    <div>
                      <Label>Areas of Expertise (0-7 scale)</Label>
                      <div className="space-y-3 mt-2">
                        {expertiseOptions.map((option) => (
                          <div key={option} className="space-y-1">
                            <div className="flex justify-between">
                              <Label className="text-sm">{option}</Label>
                              <span className="text-xs font-semibold text-primary">
                                {expertisePoints[option] || 0}
                              </span>
                            </div>
                            <Slider
                              value={[expertisePoints[option] || 0]}
                              onValueChange={(value) => {
                                setExpertisePoints({
                                  ...expertisePoints,
                                  [option]: value[0],
                                });
                              }}
                              max={7}
                              step={1}
                              className="w-full"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="degree">Highest Degree</Label>
                      <Select
                        value={formFields.degree}
                        onValueChange={(value) =>
                          setFormFields({ ...formFields, degree: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select degree" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High School">
                            High School
                          </SelectItem>
                          <SelectItem value="B.Sc.">B.Sc.</SelectItem>
                          <SelectItem value="B.A.">B.A.</SelectItem>
                          <SelectItem value="M.Sc.">M.Sc.</SelectItem>
                          <SelectItem value="M.A.">M.A.</SelectItem>
                          <SelectItem value="MBA">MBA</SelectItem>
                          <SelectItem value="Ph.D.">Ph.D.</SelectItem>
                          <SelectItem value="J.D.">J.D.</SelectItem>
                          <SelectItem value="CPA">CPA</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="degreeYear">Year of Degree</Label>
                      <Input
                        id="degreeYear"
                        type="number"
                        value={formFields.degreeYear}
                        onChange={(e) =>
                          setFormFields({
                            ...formFields,
                            degreeYear: e.target.value,
                          })
                        }
                        placeholder="e.g., 2015"
                      />
                    </div>

                    <div>
                      <Label htmlFor="institution">Institution</Label>
                      <Input
                        id="institution"
                        defaultValue={selectedMember.institution || ""}
                      />
                    </div>

                    <div>
                      <Label htmlFor="institutionCountry">
                        Country of Institution
                      </Label>
                      <Select
                        value={formFields.institutionCountry}
                        onValueChange={(value) =>
                          setFormFields({
                            ...formFields,
                            institutionCountry: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select country" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="US">United States</SelectItem>
                          <SelectItem value="UK">United Kingdom</SelectItem>
                          <SelectItem value="CA">Canada</SelectItem>
                          <SelectItem value="AU">Australia</SelectItem>
                          <SelectItem value="DE">Germany</SelectItem>
                          <SelectItem value="FR">France</SelectItem>
                          <SelectItem value="NL">Netherlands</SelectItem>
                          <SelectItem value="CH">Switzerland</SelectItem>
                          <SelectItem value="SE">Sweden</SelectItem>
                          <SelectItem value="NO">Norway</SelectItem>
                          <SelectItem value="DK">Denmark</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Column 3: Personal Information */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground border-b pb-2">
                    Personal Information
                  </h4>

                  <div className="space-y-3">
                    {/* Profile Image - Moved to top and larger */}
                    <div>
                      <Label htmlFor="profileImage">Profile Image</Label>
                      <div className="mt-2 space-y-3">
                        {/* Hidden file input */}
                        <input
                          ref={profileImageInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleProfileImageChange}
                          className="hidden"
                        />

                        {/* Profile image preview - clickable to change */}
                        {selectedMember.image && (
                          <div
                            className="w-32 h-32 rounded-full overflow-hidden mx-auto cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() =>
                              profileImageInputRef.current?.click()
                            }
                            title="Click to change image"
                          >
                            <img
                              src={selectedMember.image}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}

                        {/* Upload button */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 w-full"
                          onClick={() => profileImageInputRef.current?.click()}
                        >
                          <Upload className="w-4 h-4" />
                          {selectedMember.image
                            ? "Change Image"
                            : "Upload Image"}
                        </Button>
                        
                        {/* Image specs note */}
                        <div className="text-xs text-muted-foreground space-y-1 mt-2 p-2 bg-muted/30 rounded">
                          <p className="font-semibold">Board Member Image Rules:</p>
                          <p><strong>File format:</strong> JPEG/JPG, PNG, GIF, WebP</p>
                          <p><strong>File size:</strong> Maximum 5MB (files larger than 5MB will be rejected)</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="lgbtqia">LGBTQIA+ Identity</Label>
                      <Select
                        value={formFields.lgbtqiaIdentity}
                        onValueChange={(value) =>
                          setFormFields({
                            ...formFields,
                            lgbtqiaIdentity: value,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select identity" />
                        </SelectTrigger>
                        <SelectContent>
                          {lgbtqiaOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="specialRequirements">
                        Special Requirements
                      </Label>
                      <Textarea
                        id="specialRequirements"
                        value={formFields.specialRequirements}
                        onChange={(e) =>
                          setFormFields({
                            ...formFields,
                            specialRequirements: e.target.value,
                          })
                        }
                        placeholder="e.g., wheelchair access, hearing aid, handicap parking"
                      />
                    </div>

                    <div>
                      <Label htmlFor="dietaryRequirements">
                        Dietary Requirements
                      </Label>
                      <Textarea
                        id="dietaryRequirements"
                        value={formFields.dietaryRequirements}
                        onChange={(e) =>
                          setFormFields({
                            ...formFields,
                            dietaryRequirements: e.target.value,
                          })
                        }
                        placeholder="e.g., gluten intolerance, vegan"
                      />
                      <div className="mt-2">
                        {/* Hidden file input for dietary document */}
                        <input
                          ref={dietaryDocumentRef}
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleDietaryDocumentChange}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                          onClick={() => dietaryDocumentRef.current?.click()}
                        >
                          <Upload className="w-4 h-4" />
                          {selectedMember.dietaryDocumentFile
                            ? `Selected: ${selectedMember.dietaryDocumentFile.name}`
                            : "Upload Document"}
                        </Button>
                      </div>

                      {/* Display existing dietary documents */}
                      {selectedMember.id && dietaryDocuments.length > 0 && (
                        <div className="mt-3 pt-3 border-t space-y-2">
                          <Label className="text-xs font-medium text-muted-foreground">
                            Uploaded Documents
                          </Label>
                          {dietaryDocuments.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-2 bg-muted/20 rounded text-xs"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-sm">📄</span>
                                <span className="truncate font-medium">
                                  {doc.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2"
                                  onClick={() =>
                                    handleDownloadDietaryDocument(
                                      doc.id,
                                      doc.name
                                    )
                                  }
                                  title="Download"
                                >
                                  ⬇
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-2 text-destructive hover:text-destructive"
                                  onClick={() =>
                                    handleDeleteDietaryDocument(doc.id)
                                  }
                                  title="Delete"
                                >
                                  🗑
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Leisure Activities</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {leisureActivities.map((activity) => (
                          <div
                            key={activity}
                            className="flex items-center space-x-2"
                          >
                            <Checkbox
                              id={activity}
                              checked={selectedLeisureActivities.includes(
                                activity
                              )}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedLeisureActivities([
                                    ...selectedLeisureActivities,
                                    activity,
                                  ]);
                                } else {
                                  setSelectedLeisureActivities(
                                    selectedLeisureActivities.filter(
                                      (a) => a !== activity
                                    )
                                  );
                                }
                              }}
                            />
                            <Label htmlFor={activity} className="text-sm">
                              {activity}
                            </Label>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Custom Activity
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Attendance & Education Sections - Only show for EXISTING members */}
              {selectedMember.id && (
                <>
                  <div className="mt-6 p-4 border-t">
                    <h4 className="font-semibold text-foreground mb-4">
                      Meeting Attendance Record
                    </h4>

                    {/* Attendance Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2 font-medium">Year</th>
                            <th className="text-center p-2 font-medium">Jan</th>
                            <th className="text-center p-2 font-medium">Feb</th>
                            <th className="text-center p-2 font-medium">Mar</th>
                            <th className="text-center p-2 font-medium">Apr</th>
                            <th className="text-center p-2 font-medium">May</th>
                            <th className="text-center p-2 font-medium">Jun</th>
                            <th className="text-center p-2 font-medium">Jul</th>
                            <th className="text-center p-2 font-medium">Aug</th>
                            <th className="text-center p-2 font-medium">Sep</th>
                            <th className="text-center p-2 font-medium">Oct</th>
                            <th className="text-center p-2 font-medium">Nov</th>
                            <th className="text-center p-2 font-medium">Dec</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b">
                            <td className="p-2 font-medium">2025</td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Jan 25 | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Feb 28 | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Mar 15 (Q1) | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-red-600 hover:underline"
                                title="Attended: None | Missed: Apr 22"
                              >
                                0/1
                              </span>
                            </td>
                            <td className="text-center p-2">-</td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-red-600 hover:underline"
                                title="Attended: None | Missed: Jun 15 (Q2)"
                              >
                                0/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Jul 18 | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Aug 15, Aug 16 (Workshop Days 1&2) | Missed: None"
                              >
                                2/2
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Sep 15 (Q3) | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Oct 10 | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Nov 12 | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                            <td className="text-center p-2">
                              <span
                                className="cursor-pointer text-green-600 hover:underline"
                                title="Attended: Dec 15 (Q4) | Missed: None"
                              >
                                1/1
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="flex items-center justify-center gap-6 pt-4 border-t mt-4">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-500" />
                        <span className="text-sm font-medium">10 Attended</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <X className="w-5 h-5 text-red-500" />
                        <span className="text-sm font-medium">2 Missed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground/80">
                          Overall: 83%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Board Education Participation */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-3">
                      Board Education Participation
                    </h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs p-2 bg-muted/20 rounded">
                        <div>
                          <p className="font-medium">
                            Corporate Governance Best Practices
                          </p>
                          <p className="text-foreground/60">Feb 15, 2024</p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                          Completed
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs p-2 bg-muted/20 rounded">
                        <div>
                          <p className="font-medium">
                            Financial Oversight & Risk Management
                          </p>
                          <p className="text-foreground/60">May 20, 2024</p>
                        </div>
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                          In Progress
                        </span>
                      </div>
                      <div className="flex justify-between items-center text-xs p-2 bg-muted/20 rounded">
                        <div>
                          <p className="font-medium">
                            ESG Compliance & Sustainability
                          </p>
                          <p className="text-foreground/60">Aug 10, 2024</p>
                        </div>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          Not Started
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div className="flex justify-between gap-2 mt-6">
                <div className="flex gap-2">
                  {/* Export/backup as PDF - only show for existing members */}
                  {selectedMember.id && (
                    <Button variant="outline" onClick={exportMemberAsPdf}>
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  )}

                  {/* Delete member with confirmation dialog - only show for existing members */}
                  {selectedMember.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive">Delete Member</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Are you sure you want to delete this board member?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This action will permanently delete{" "}
                            <strong>{selectedMember.name}</strong> and all their
                            associated information. This cannot be undone.
                            Consider exporting their data as PDF before deleting.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={async () => {
                              try {
                                await deleteBoardMember(selectedMember.id);
                                alert("Board member deleted successfully");
                                setIsFileCardOpen(false);
                                loadBoardMembers(); // Refresh the list
                              } catch (error) {
                                console.error(
                                  "Failed to delete board member:",
                                  error
                                );
                                alert(
                                  "Failed to delete board member. Please try again."
                                );
                              }
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
                <div className="flex gap-2 ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => setIsFileCardOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveMember}>Save Changes</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BoardMemberGallery;
